<template>
  <page-template :is-loading="isLoading" :data-exists="dynamicRules !== null">
    <template #pageHeadline>
      {{ $t('dynamicRule.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="dynamicRules"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="dynamic-rules-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewDynamicRulePath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateDynamicRule"
        @delete-row-button-clicked="openDeleteDynamicRuleModal"
        @download-data="downloadDoc"
      >
        <template v-if="showFilteringIpBlockingSlot" #offloadIpBlockingSlot="cellProps">
          <messages-badge
            v-if="cellProps.row.offload_ip_filtering"
            :message="$t('dynamicRule.listPage.offloadModeOn')"
            color-variant="full-info"
          >
          </messages-badge>
        </template>
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteDynamicRuleModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteDynamicRule"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteDynamicRuleModal = false"
        @closeModal="showDeleteDynamicRuleModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedDynamicRule.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Action, DocumentName, DynamicRule } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  shouldUseMockData,
  getDynamicRulesMockData,
  getActionsMockData,
} from '@/helpers/MockDataUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import MessagesBadge from 'ui-component-library/rbz/components/MessagesBadge.vue';
import { ENV } from '@/constants';
import { TARGET_OPTIONS } from '@/helpers/Consts';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'DynamicRulesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    MessagesBadge,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getDynamicRulePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      dynamicRules: null as DynamicRule[],
      actions: [] as Action[],
      pageLoadingCounter: null,
      targetOptions: _.map(TARGET_OPTIONS, (targetOption) => {
        return {
          key: targetOption.key,
          title: this.$t(targetOption.translationKey),
        };
      }),
      showDeleteDynamicRuleModal: false,
      selectedDynamicRule: null,
      publicCloudProviders: ['gcp', 'aws'],
      provider: ENV.VITE_LOAD_BALANCER_PROVIDER,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Dynamic Rules List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadDynamicRules();
          await this.loadActions();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('dynamicRule.plural'),
        singular: this.$t('dynamicRule.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    showFilteringIpBlockingSlot(): boolean {
      return this.provider && !this.publicCloudProviders.includes(this.provider);
    },

    columns(): RbzTableColumnOptions[] {
      const columnsArray = [
        {
          id: 'name',
          title: this.$t('dynamicRule.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getDynamicRulePath(item);
          },
        },
        {
          id: 'target',
          title: this.$t('dynamicRule.listPage.target'),
          fieldNames: ['target'],
          displayFunction: (item: DynamicRule) => {
            return this.targetColumn(item);
          },
          tooltipFunctionAlwaysDisplayed: true,
          cellContentClasses: 'ellipsis',
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px',
        },
        {
          id: 'active',
          title: this.$t('dynamicRule.listPage.active'),
          fieldNames: ['active'],
          displayFunction: (item: DynamicRule) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
        },
        {
          id: 'time-frame',
          title: this.$t('dynamicRule.listPage.timeFrame'),
          fieldNames: ['timeframe'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'threshold',
          title: this.$t('dynamicRule.listPage.threshold'),
          fieldNames: ['threshold'],
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'action',
          title: this.$t('dynamicRule.listPage.action'),
          fieldNames: ['action'],
          displayFunction: (item: DynamicRule) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px action-cell',
          cellContentClasses: 'ellipsis',
        },
      ];
      if (this.showFilteringIpBlockingSlot) {
        columnsArray.splice(1, 0, {
          id: 'offload-ip-blocking',
          title: this.$t('dynamicRule.listPage.offloadIpBlocking'),
          fieldNames: ['offload_ip_filtering'],
          displaySlotName: 'offloadIpBlockingSlot',
          isSortable: true,
          isSearchable: true,
          classes: 'width-220px',
        });
      }
      return columnsArray;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadDynamicRules() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getDynamicRulesMockData() };
      } else {
        const url = `conf/${this.selectedBranch}/dynamic-rules`;
        const fieldNames = _.flatMap(this.columns, 'fieldNames');
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url,
          config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
        });
      }
      this.dynamicRules = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      let response;
      if (shouldUseMockData()) {
        response = { data: getActionsMockData() };
      } else {
        response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'GET',
          url: `conf/${this.selectedBranch}/actions`,
          config: { headers: { 'x-fields': 'id, name' } },
        });
      }
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    getDynamicRulePath(dynamicRule: DynamicRule) {
      return this.getEditPath('dynamic-rules', dynamicRule.id);
    },

    getNewDynamicRulePath() {
      return this.getNewPath('dynamic-rules');
    },

    editDynamicRule(dynamicRule: DynamicRule) {
      this.navigateTo(this.getDynamicRulePath(dynamicRule));
    },

    async duplicateDynamicRule(dynamicRule: DynamicRule) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/dynamic-rules/${dynamicRule.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalDynamicRule = getOriginalResponse.data;
      const dynamicRuleToAdd = _.cloneDeep(originalDynamicRule);
      dynamicRuleToAdd.id = DatasetsUtils.generateUUID2();
      dynamicRuleToAdd.name = Utils.generateUniqueEntityName(
        dynamicRuleToAdd.name,
        _.map(this.dynamicRules, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/dynamic-rules/${dynamicRuleToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: dynamicRuleToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editDynamicRule(dynamicRuleToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteDynamicRule() {
      this.setPageLoadingStatus(true);
      const dynamicRuleId = this.selectedDynamicRule.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/dynamic-rules/${dynamicRuleId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.dynamicRules = _.filter(this.dynamicRules, (dynamicRule) => {
          return dynamicRule.id !== dynamicRuleId;
        });
      }
      this.showDeleteDynamicRuleModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('dynamic-rules', 'json', this.dynamicRules);
    },

    openDeleteDynamicRuleModal(dynamicRule: DynamicRule) {
      this.showDeleteDynamicRuleModal = true;
      this.selectedDynamicRule = dynamicRule;
    },

    targetColumn(dynamicRule: DynamicRule): string {
      const splitTarget = dynamicRule.target.includes('_') ? dynamicRule.target.split('_') : null;
      const dynamicRuleTarget = splitTarget ? splitTarget[0] : dynamicRule.target;
      const targetOption = this.targetOptions.find((option) => {
        return dynamicRuleTarget === option.key;
      });
      if (!targetOption) {
        return '';
      }
      if (splitTarget && splitTarget.length > 1) {
        return targetOption.title + ':' + splitTarget.slice(1).join('_');
      }
      return targetOption.title;
    },
  },
});
</script>
